import axios, { CancelTokenSource, AxiosRequestConfig } from "axios";
import { ACCOUNT_EXIST, ACCOUNT_SUSPENDED, TOKEN_EXPIRED } from "../api/code";

export const LOCAL_STORAGE_ACCESS_KEY = "runner_access_token";

const network = axios.create({
  baseURL: process.env.REACT_APP_WEB_API_HOST + "",
});

// 로딩 상태 관리를 위한 변수들
let loadingCount = 0;
let setLoadingState: ((loading: boolean) => void) | null = null;

// 로딩 상태 설정 함수 등록 (앱 초기화 시 호출) 리코일 못 써서 그래요
export function setLoadingStateSetter(setter: (loading: boolean) => void) {
  setLoadingState = setter;
}

// 로딩 상태 업데이트 함수
function updateLoadingState() {
  if (setLoadingState) {
    setLoadingState(loadingCount > 0);
  }
}

// 로딩 시작
function startLoading() {
  loadingCount++;
  updateLoadingState();
}

// 로딩 종료
function endLoading() {
  loadingCount = Math.max(0, loadingCount - 1);
  updateLoadingState();
}

// 체크용
interface PendingRequest {
  cancelTokenSource: CancelTokenSource;
  timer: NodeJS.Timeout;
}

const pendingRequests: { [key: string]: PendingRequest } = {};
const DEBOUNCE_DELAY = 100;

// 요청의 고유 키를 생성하는 함수
function getRequestKey(config: AxiosRequestConfig): string {
  return `${config.method}:${config.url}`;
}

network.interceptors.request.use(async (config) => {
  const requestKey = getRequestKey(config);

  // 이전 같은 요청이 있다면 취소
  if (pendingRequests[requestKey]) {
    pendingRequests[requestKey].cancelTokenSource.cancel(
      "Duplicate request canceled"
    );
    clearTimeout(pendingRequests[requestKey].timer);
    endLoading(); // 취소된 요청의 로딩 상태 해제
  }

  // 새로운 CancelToken 생성
  const cancelTokenSource = axios.CancelToken.source();
  config.cancelToken = cancelTokenSource.token;

  // 로딩 안할 api
  const excludedPaths = [
    "/runner-rankings",
    "/version",
    "snslogin",
    "/pubs",
    "youtube-live/list",
  ];
  const shouldIntercept = excludedPaths.every(
    (path) => !requestKey.includes(path)
  );

  // GET 요청은 로딩 제외
  const isGetRequest = config.method?.toLowerCase() === "get";

  if (shouldIntercept && !isGetRequest) {
    // 로딩 시작
    startLoading();
  }

  // 취소 로직
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      const headers = config.headers;
      const accessToken = localStorage.getItem(LOCAL_STORAGE_ACCESS_KEY);
      if (accessToken !== null) {
        headers["Authorization"] = accessToken;
      }
      delete pendingRequests[requestKey]; // 요청이 시작되면 pending 목록에서 제거
      resolve(config);
    }, DEBOUNCE_DELAY);

    pendingRequests[requestKey] = { cancelTokenSource, timer };
  });
});

network.interceptors.response.use(
  async (res) => {
    const requestKey = getRequestKey(res.config);
    delete pendingRequests[requestKey]; // 응답이 오면 pending 목록에서 제거

    // 로딩 종료
    endLoading();

    if (res.data) {
      if (res.data.code && res.data.code !== 200) {
        throw res.data;
      }

      if (res.data.tokenType && res.data.accessToken && res.data.refreshToken) {
        localStorage.setItem(
          LOCAL_STORAGE_ACCESS_KEY,
          `${res.data.tokenType} ${res.data.accessToken}`
        );
      }

      return res.data;
    }
  },
  async (e: any) => {
    // 로딩 종료
    endLoading();

    if (axios.isCancel(e)) {
      console.log("Request canceled:", e.message);
      return;
    }

    const requestKey = e.config ? getRequestKey(e.config) : null;
    if (requestKey) {
      delete pendingRequests[requestKey]; // 에러 발생 시에도 pending 목록에서 제거
    }

    let errorMsg = e.message;

    if (e.response) {
      if (e.response.status === 400) {
        if (e.response.data) {
          if (e.response.data.message) {
            errorMsg = `(${e.response.data.code}) ${e.response.data.message}`;
          }

          switch (e.response.data.code) {
            case TOKEN_EXPIRED:
              try {
                // 토큰 갱신 요청도 로딩 처리
                startLoading();
                localStorage.removeItem(LOCAL_STORAGE_ACCESS_KEY);
                const response = await axios.post(
                  `${process.env.REACT_APP_WEB_API_HOST}/auth/refresh-token`,
                  null,
                  {
                    withCredentials: true,
                  }
                );
                endLoading();

                if (response && response.status === 200 && response.data) {
                  localStorage.setItem(
                    LOCAL_STORAGE_ACCESS_KEY,
                    `${response.data.tokenType} ${response.data.accessToken}`
                  );
                  window.location.href = "/";
                }
              } catch (e) {
                endLoading();
              }

              break;
            case ACCOUNT_SUSPENDED:
            case ACCOUNT_EXIST:
              localStorage.removeItem(LOCAL_STORAGE_ACCESS_KEY);
              break;
          }
        }
      }
    } else {
      throw e;
    }

    throw {
      ...e,
      statusCode: e.response.status,
      code: e.response.data?.code,
      message: errorMsg,
      payload: e.response.data?.payload,
    };
  }
);

export function hasCredential() {
  return localStorage.getItem(LOCAL_STORAGE_ACCESS_KEY) !== null;
}

export function clearCredential() {
  localStorage.removeItem(LOCAL_STORAGE_ACCESS_KEY);
}

export default network;
